<template>
  <SfBannerInline
    v-if="showAlert"
    :variant="bannerVariants.warning"
    :show-icon="false"
    single-line
    class="sf-banner--no-border"
    data-testid="unsaved-changes-alert"
  >
    <template #title>
      <span class="tw-text-nowrap">
        {{ bannerTitle }}
      </span>
    </template>

    <template #actions>
      <span
        v-if="partnerHandBack"
        v-tooltip.placementLeft.textLeft="
          getActiveFilters.length
            ? ''
            : I18n.t('companies_overview.segments_new.hand_back_needs_filter')
        "
      >
        <SfButtonDs
          size="sm"
          :disabled="!canHandBack"
          data-testid="unsaved-changes-partner-update"
          @click="handleUpdatePartnerSegment"
        >
          {{
            I18n.t('companies_overview.segments_new.update_in_partner_section')
          }}
        </SfButtonDs>
      </span>

      <SfDropdown
        v-else
        :dropdown-id="dropdownId"
        :is-open="isDropdownOpen"
        variant="seamless"
        unstyled-trigger
        drawer-direction="left"
        @dropdown-clicked="toggleDropdown"
        @dropdown-closed="closeDropdown"
      >
        <template #trigger>
          <SfButtonDs size="sm" data-testid="unsaved-changes-save-btn">
            {{ I18n.t('companies_overview.progress_v2.save') }}
            <span class="fa fa-caret-down tw-ml-1" aria-hidden="true" />
          </SfButtonDs>
        </template>

        <template #menu>
          <div
            v-if="showUpdateQuery"
            v-tooltip.placementLeft.textLeft="
              marketSegment
                ? I18n.t(
                    'companies_overview.segments_new.market_segment_cannot_save'
                  )
                : ''
            "
          >
            <SfDropdownItem
              :is-tall="true"
              :is-disabled="marketSegment"
              data-testid="unsaved-changes-update-query"
              @click="handleUpdateQuery"
            >
              <div>{{ I18n.t('companies_overview.segments_new.update') }}</div>
            </SfDropdownItem>
          </div>

          <SfDropdownItem
            :is-tall="true"
            data-testid="unsaved-changes-save-as-new"
            @click="handleSaveAsNew"
          >
            <div>{{ saveAsLabel }}</div>
          </SfDropdownItem>

          <SfDropdownItem
            :is-tall="true"
            data-testid="unsaved-changes-discard"
            @click="handleDiscard"
          >
            <div class="tw-text-sf-danger-light-1">
              {{ I18n.t('companies_overview.progress_v2.discard_changes') }}
            </div>
          </SfDropdownItem>
        </template>
      </SfDropdown>
    </template>
  </SfBannerInline>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import I18n from 'i18n-js'

import { SfLog } from '@app/adapters/logger'
import { useDropdown } from '@app/shared/pinia/dropdown'
import { dropdowns } from '@app/shared/types/dropdown'
import { bannerVariants } from '@app/shared/types/banner'
import { partnerSegmentParams } from '@app/insights/utils/partner-editing'
import { useFiltersStore } from '@app/insights/store/filters'
import { useSegmentsStore } from '@app/insights/store/segments'
import { useInsightsRootStore } from '@app/insights/store/insights-root'
import SfBannerInline from '@app/shared/vue/components/sf-banner/sf-banner-inline.vue'
import { SfButtonDs } from '@silverfin/ui-components'
import {
  SfDropdown,
  SfDropdownItem,
} from '@app/shared/vue/components/sf-dropdown'

defineOptions({
  name: 'InsightsUnsavedChangesAlert',
})

const segmentsStore = useSegmentsStore()
const filtersStore = useFiltersStore()
const rootStore = useInsightsRootStore()
const dropdownStore = useDropdown()

const { segment, isSegmentModified, isSegmentLoading } =
  storeToRefs(segmentsStore)
const { getActiveFilters } = storeToRefs(filtersStore)
const { currentDropdownId } = storeToRefs(dropdownStore)

const dropdownId = dropdowns.INSIGHTS_SAVE_QUERY
const isDropdownOpen = computed(() => currentDropdownId.value === dropdownId)

const hasSavedQuery = computed(() => !!segment.value?.id)
// A market segment opened from the partner section (ms_id) is saved back
// there rather than onto a firm query, so this alert offers a single hand-back
// action instead of the save dropdown. Read from the URL, not vue-router: this
// component is mounted in the subnav mini-app, which has no router.
const partnerSegment = partnerSegmentParams()
// The URL params outlive an in-page segment load, so a loaded firm query still
// looks like partner mode: handing it back would overwrite the partner segment.
const partnerHandBack = computed(() => !!partnerSegment && !hasSavedQuery.value)
// The receiving form rebuilds the partner segment's filters from the payload,
// so an empty set destroys the filters it already has.
const canHandBack = computed(
  () =>
    partnerHandBack.value &&
    !isSegmentLoading.value &&
    getActiveFilters.value.length > 0
)
// The all-clients query has no saved baseline: any active filter or period
// selection is an unsaved change.
const hasPeriodSelected = computed(
  () =>
    !!rootStore.ledgerSelectDate ||
    !!rootStore.ledgerSelectLockType ||
    !!rootStore.periodSelection
)
const allClientsModified = computed(
  () =>
    !hasSavedQuery.value &&
    (getActiveFilters.value.length > 0 || hasPeriodSelected.value)
)

const showAlert = computed(
  () =>
    rootStore.queryOverviewEnabled &&
    (partnerHandBack.value ||
      isSegmentModified.value ||
      allClientsModified.value)
)

const bannerTitle = computed(() =>
  partnerHandBack.value
    ? I18n.t('companies_overview.segments_new.editing_partner_segment', {
        name: partnerSegment?.name,
      })
    : I18n.t('companies_overview.progress_v2.unsaved_changes')
)

const marketSegment = computed(() => !!segment.value?.partner)
const showUpdateQuery = computed(
  () => !!segment.value?.permissions?.update && !segment.value?.recommended
)

const saveAsLabel = computed(() =>
  hasSavedQuery.value
    ? I18n.t('companies_overview.segments_new.save_as_new')
    : I18n.t('companies_overview.segments_new.save_as')
)

const toggleDropdown = (): void => dropdownStore.toggleDropdown(dropdownId)
const closeDropdown = (): void => dropdownStore.closeDropdown()

// Only a rejected update answers with {errorMessage}; other failures carry none.
function updateErrorMessage(e: unknown): string {
  const body = (e as { body?: { errorMessage?: string } } | null)?.body
  return body?.errorMessage || I18n.t('common.error.try_again')
}

function handleUpdatePartnerSegment(): void {
  if (!partnerSegment || !canHandBack.value) return

  segmentsStore.updatePartnerSegment(Number(partnerSegment.id))
}

function handleUpdateQuery(): void {
  if (marketSegment.value) return
  closeDropdown()

  window.sfConfirm({
    question: I18n.t('companies_overview.segments_new.confirm_update'),
    onConfirm: async () => {
      try {
        await segmentsStore.updateSegment()
      } catch (e) {
        SfLog.warn('Error updating Insights segment from the toolbar alert', {
          extra: {
            firmId: window.Silverfin.currentFirmId,
            segmentId: segment.value?.id,
            error: e,
          },
        })
        window.SilverfinFlash.addFlash('error', updateErrorMessage(e))
      }
    },
  })
}

function handleSaveAsNew(): void {
  closeDropdown()
  if (hasSavedQuery.value) {
    segmentsStore.openSaveAsSegmentModal()
  } else {
    segmentsStore.openSaveSegmentModal()
  }
}

function handleDiscard(): void {
  closeDropdown()
  if (!segment.value?.id) {
    rootStore.setLedgerSelectDate(null)
    rootStore.setLedgerSelectLockType(null)
    rootStore.setPeriodSelection(null)
    // Assigning a fresh empty array fires the main app's deep activeFilters
    // watcher even when no filters were set, re-running the query and
    // clearing the period params from the URL.
    filtersStore.unsetActiveFilters()
    return
  }

  void segmentsStore.loadSegment({
    id: segment.value.id,
    partner_segment_id: segment.value.partner_segment_id,
  })
}
</script>
